import { ICustomerUser } from './customer.dto';

export class NewConsultationDTO {
  type: string;
  answers: Answers;
  customerId?: string;
  status?: string;
  provinceCode?: string;
  orderId?: string;
  meta?: any;
  createdBy?: string;
  internal?: boolean;
}

export class UpdateConsultationDTO {
  type?: string;
  answers?: Answers;
  status?: IConsultationStatus;
  provinceCode?: string;
  orderId?: string;
  meta?: any;
}

/** for customer account consultations list */

export class Answers {
  [field: string]: any;
}

export class Answer {
  field: string;
  data: any;
}

export class Files {
  [resourceId: string]: any;
}

export class FileItem {
  field?: string;
  resourceId: string;
  buffer: string;
}

export class IConsultation<TAnswers extends Answers = Answers> {
  _id: string;
  organization: string;
  type: any; //FormTypeKeys; // TODO: fix this, should not import from lib/data
  customer: ICustomerUser | string;
  status: IConsultationStatus;
  statusHistory: ConsultationStatusHistory[];
  answers: TAnswers;
  createdAt?: Date;
  updatedAt?: Date;
  provinceCode?: string;
  orderId?: string;
  meta?: any;
  internal?: boolean;
  expiryDate?: Date;
  createdBy?: string;
}

export class ConsultationStatusHistory {
  prevStatus: IConsultationStatus;
  newStatus: IConsultationStatus;
  updatedAt: Date;
  userId: string;
}

/** @deprecated use IConsultation instead */
export class Consultation extends IConsultation {} // for backward compatibility

export class IGetConsultationsDTO {
  internal?: boolean;
  customer?: string;
  customerName?: string;
  status?: string;
  excludeStatus?: string | string[];
  type?: string;
  types?: string[];
  excludeType?: string[];
  provinceCode?: string;
  // Legacy cursor markers, kept for backwards compatibility.
  minId?: string;
  maxId?: string;
  // Server-side (offset) pagination.
  limit?: number;
  offset?: number;
  includeCount?: boolean;
  sortField?: string;
  sortOrder?: 1 | -1;
}

export type IConsultationStatus =
  | 'assigned'
  | 'incomplete'
  | 'new'
  | 'pending-input'
  | 'inprogress'
  | 'wating-for-labwork'
  | 'lab-results-in'
  | 'ready-for-np'
  | 'completed'
  | 'cancelled'
  | 'rejected'
  | 'archived';
// export class Consultation extends IConsultation {}

export type QuestionSelector =
  | 'question-info'
  | 'question-select'
  | 'question-polar-check'
  | 'question-file-upload'
  | 'question-image-list'
  | 'question-esign';

export type QuestionType =
  | 'info'
  | 'select'
  | 'polar_check'
  | 'file_upload'
  | 'image_list'
  | 'evaluated'
  | 'esign'
  | 'witness-esign';

/** Nested answer config for weight/height/BMI capture on info questions */
export type IBmiAnswerConfig = {
  type: 'bmi';
  showLabel?: boolean;
  bmiLabel?: string;
  heightLabel?: string;
  weightLabel?: string;
  showHeight?: boolean;
  showWeight?: boolean;
  /** Hide calculated BMI/height/weight summaries while retaining the inputs. */
  showCalculatedValues?: boolean;
  eligibilityCriteria?: {
    minBMI?: number;
    maxBMI?: number;
  };
};

export class IConsultTooltip {
  title: string;
  info: string;
  lineBreak?: boolean;
}

type QuestionModelExtras = {
  sendToBeluga?: boolean;
  [key: string]: any;
};

export class IQuestionModel {
  label: string | { text: string; align: 'center' | undefined; icon: string };
  type: QuestionType;
  isLast?: boolean;
  tooltip?: IConsultTooltip[];
  educationalSlide?: string;
  extras?: QuestionModelExtras = {
    sendToBeluga: true
  };
  blockQuestionFlow?: boolean = false;
  reject?: boolean = false;
  /** for file_upload type, the accepted file types */
  accepts?: string[];
  field: string;
  /** Optional CSS class applied to the question's section */
  class?: string;
  answer:
    | { type: 'text'; label: string; name: string }
    | {
        type: 'list';
        items: { type: QuestionType; label: string; options: any[] }[];
      }
    | {
        type: 'select';
        items: {
          type: QuestionType;
          label: string;
        }[];
      }
    | {
        type: 'single_select';
        options: { key: string; value: string; class?: string }[];
      }
    | {
        type: 'multi_select';
        options: {
          key: string;
          value: string;
          class?: string;
          block?: boolean;
          uncheckOthers?: boolean;
        }[];
      }
    | any;
  [key: string]: any;
}
type GetPossibleKeys<Q> = Q extends {
  answer: {
    options: readonly { key: infer Key extends string }[];
  };
}
  ? Key
  : Q extends {
      answer: {
        options: Array<{ key: infer Key extends string }>;
      };
    }
  ? Key
  : Q extends {
      answer: {
        name: infer Name extends string;
      };
    }
  ? Name
  : never;

// Map question types to objects with specific boolean fields
export type QuestionAnswerMap<T extends readonly any[]> = {
  [Q in T[number] as Q['field']]: Partial<{
    [K in GetPossibleKeys<Q>]: boolean;
  }>;
};

/**
 * Union of all condition keys for a FieldAnswers type.
 * Condition keys are `${field}.${option}` used in form flow conditions.
 * Use with FormType<T> so condition objects get autocomplete.
 */
export type ConditionKey<T> = keyof T extends never
  ? string
  : T extends Record<string, Record<string, unknown>>
  ? {
      [F in keyof T]: T[F] extends Record<string, unknown>
        ? `${F & string}.${keyof T[F] & string}`
        : never;
    }[keyof T]
  : string;

export class IFormType {
  name: string;
  questions: Array<string>;
  onSubmit?: (answers: Answers) => void;
  saveState?: (answers: Answers) => object;
}

export class IRequiredFormsParam {
  types?: string;
  orderId?: number;
  redirectUrl?: string;
}

export type FlattenedQuestionType =
  | 'text'
  | 'date'
  | 'number'
  | 'text_list'
  | 'radio'
  | 'dropdown'
  | 'single_select'
  | 'multi_select'
  | 'check';

export interface IFlattenedQuestionBase {
  type: FlattenedQuestionType;
  questionLabel: string;
  questionKey: string;
  extras?: any;
}

export interface IFlattenedText extends IFlattenedQuestionBase {
  type: 'text';
  answer: string;
}

export interface IFlattenedDate extends IFlattenedQuestionBase {
  type: 'date';
  answer: string;
}

export interface IFlattenedNumber extends IFlattenedQuestionBase {
  type: 'number';
  answer: number;
}

export interface IFlattenedTextList extends IFlattenedQuestionBase {
  type: 'text_list';
  answer: string[];
}

export interface IFlattenedSelect extends IFlattenedQuestionBase {
  type: 'single_select';
  options: {
    label: string;
    key: string;
  }[];
  answer: string;
}

export interface IFlattenedCheck extends IFlattenedQuestionBase {
  type: 'check';
  answer: boolean;
}

export interface IFlattenedMultiSelect extends IFlattenedQuestionBase {
  type: 'multi_select';
  options: {
    label: string;
    key: string;
  }[];
  answer: string[];
}

export type IFlattenedQuestion =
  | IFlattenedText
  | IFlattenedDate
  | IFlattenedNumber
  | IFlattenedTextList
  | IFlattenedSelect
  | IFlattenedMultiSelect
  | IFlattenedCheck;

/**
 * Response body of GET /consultations/required-forms.
 *
 * FROZEN CONTRACT — patient-web and other legacy clients depend on this exact
 * shape, so nothing may be added or removed here. The endpoint is engine-unaware:
 * questionnaires migrated to Pathwai still appear as plain keys. Callers that
 * need to know where to send the patient use /pending-requirements instead.
 */
export interface IRequiredFormsResponse {
  /** All outstanding questionnaire keys. */
  requiredConsultsTypes: string[];
  incompleteConsults: IConsultation[];
  verificationReqs: boolean;
}

/**
 * One outstanding questionnaire returned by GET /consultations/pending-requirements.
 *
 * `engine` tells the caller who answers it: `legacy` means the caller renders
 * patient-web's own form, `pathwai` means it is handled by a Pathwai flow and
 * the patient should be sent to the response's `pathwaiStartUrl`. This is what
 * lets patient-web be decommissioned one questionnaire at a time — see
 * `organization.engage` in the org settings.
 */
export interface IRequiredConsultation {
  /** Consultation/questionnaire key (FormTypeKeys), e.g. 'wl'. */
  type: string;
  engine: 'legacy' | 'pathwai';
  /**
   * Already finished for this run — satisfied, or the patient hit a rejection
   * terminal.
   *
   * The rejected case matters: a rejected consultation never counts as
   * satisfied, so without this the requirement stays outstanding and a client
   * would hand the patient the very same questionnaire again the moment they
   * finished being declined.
   *
   * Optional because it was added after patient-web shipped against this
   * response; patient-web ignores it.
   */
  completed?: boolean;
}

/**
 * Response body of GET /consultations/pending-requirements — the Pathwai-aware
 * view of the same outstanding questionnaires as /required-forms.
 */
export interface IPendingRequirementsResponse {
  /** Outstanding questionnaires, each tagged with the engine that answers it. */
  consultations: IRequiredConsultation[];
  incompleteConsults: IConsultation[];
  verificationReqs: boolean;
  /**
   * Where to send the patient to work through the Pathwai questionnaires.
   *
   * A single entry point, not one URL per questionnaire: Pathwai re-resolves the
   * outstanding set itself, runs each matching flow in turn, and finally forwards
   * to `redirectTo`. Only present when at least one questionnaire routes to
   * Pathwai and the org's engage config is complete enough to build it.
   */
  pathwaiStartUrl?: string;
}
